import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys, STALE_TIMES } from '@/lib/queryKeys';
import { PageLoader } from './PageLoader';
import { Card } from './ui/card';
import { ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';

interface AdminGuardProps {
  children: React.ReactNode;
  description?: string;
}

export const AdminGuard = ({
  children,
  description = 'This page is restricted to administrators only.',
}: AdminGuardProps) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Shared cache key + 5-min staleTime so repeated AdminGuard mounts
  // (or other role-aware components) reuse the same fetch instead of
  // re-querying user_roles every navigation.
  const {
    data: isAdmin,
    isLoading: roleLoading,
  } = useQuery({
    queryKey: queryKeys.userRole(user?.id),
    enabled: !!user && !authLoading,
    staleTime: STALE_TIMES.userRole,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!user) return false;
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
  });

  if (authLoading || (!!user && roleLoading)) {
    return <PageLoader />;
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full p-8 space-y-6 text-center animate-fade-in">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-destructive/10 border border-destructive/20">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">Access Restricted</h2>
            <p className="text-muted-foreground">{description}</p>
          </div>
          <Button onClick={() => navigate('/chat')} className="w-full" variant="default">
            Return to Chat
          </Button>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
